import {
  APPROVAL_REJECTED,
  APPROVED,
  AWAITING_APPROVAL,
  AWAITING_SIGNATURE,
  DRAFT,
  EXECUTED,
  EXPIRED,
  SENDING,
  SEND_FAILED,
  SIGNATURE_DECLINED,
  VOIDED,
  WITHDRAWN,
} from 'v2/helpers/status/loi';
import { handleOpenNewTab } from '../useActions/common';

const documentCreatorUrl = (row) =>
  `/document-creator/template/${row.loi_id}/loi/${row.tender_id}`;

const downloadUrl = (row) => `/download-manager/loi/${row.loi_id}`;

const editLoi = (row) => handleOpenNewTab('edit-loi', documentCreatorUrl(row));
const viewLoi = (row, url) =>
  handleOpenNewTab('view-loi', url || documentCreatorUrl(row));
const downloadLoi = (row, url) =>
  handleOpenNewTab('download-loi', url || downloadUrl(row));

const viewApprovalHistory = (row, { onToggleExpand, rowKey }) => ({
  name: 'view-approval-history',
  action: () => onToggleExpand(rowKey),
});

const withdrawApproval = (row, { onWithdrawApproval }) => ({
  name: 'withdraw-loi-approval',
  action: () => onWithdrawApproval(row),
});

const createFinalOrder = (row, { onCreateFinalOrder }) => ({
  name: 'create-final-order',
  action: () => onCreateFinalOrder(row),
});

const viewLogs = (row, { onViewLogs }) => ({
  name: 'view-logs',
  action: () => onViewLogs(row),
});

const getLoiActions = (row, status, handlers = {}) => {
  const {
    onToggleExpand,
    onWithdrawApproval,
    onCreateFinalOrder,
    onViewLogs,
    hasApprovers,
  } = handlers;

  if (!row?.loi_id || !row?.tender_id) return [];

  const history =
    hasApprovers && onToggleExpand ? [viewApprovalHistory(row, handlers)] : [];
  const logs = onViewLogs ? [viewLogs(row, handlers)] : [];

  switch (status) {
    case DRAFT:
      return [editLoi(row), ...logs];

    case AWAITING_APPROVAL:
      return [
        viewLoi(row),
        ...history,
        ...logs,
        ...(onWithdrawApproval ? [withdrawApproval(row, handlers)] : []),
      ];

    case APPROVAL_REJECTED:
      return [editLoi(row), ...history, ...logs];

    case APPROVED:
      return [viewLoi(row), downloadLoi(row), ...history, ...logs];

    case SENDING:
      return [viewLoi(row), ...history, ...logs];

    case SEND_FAILED:
      return [editLoi(row), ...history, ...logs];

    case AWAITING_SIGNATURE:
      return [viewLoi(row), downloadLoi(row), ...history, ...logs];

    case EXECUTED: {
      const signedUrl = row.transaction_id
        ? `/signatory/download/${row.transaction_id}?document_id=${row.loi_id}`
        : null;
      return [
        viewLoi(row, signedUrl),
        downloadLoi(row, signedUrl),
        ...history,
        ...logs,
        ...(onCreateFinalOrder ? [createFinalOrder(row, handlers)] : []),
      ];
    }

    // An expired LOI still unlocks the final order, so the commitment it
    // covered can be carried into a full order.
    case EXPIRED:
      return [
        viewLoi(row),
        downloadLoi(row),
        ...history,
        ...logs,
        ...(onCreateFinalOrder ? [createFinalOrder(row, handlers)] : []),
      ];

    case SIGNATURE_DECLINED:
    case WITHDRAWN:
    case VOIDED:
      return [viewLoi(row), downloadLoi(row), ...history, ...logs];

    default:
      return [viewLoi(row), ...logs];
  }
};

export default getLoiActions;
